import { MoreVert } from "@mui/icons-material";
import {
  Box,
  Divider,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import React from "react";
import ReactDOM from "react-dom";
import { IContextMenuProps, IMenuOption } from "./interfaces/IContextMenuProps";

export const ContextMenu: React.FC<IContextMenuProps> = (props) => {
  const { title, menuOptions } = props;
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const menuRef = React.useRef<HTMLDivElement>(null);

  const handleMouseDown = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    event.preventDefault();
  };

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    event.preventDefault();
    setAnchorEl((prev) => (prev ? null : event.currentTarget));
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMenuItemClick = (menuItem: IMenuOption) => (event: React.MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();
    setAnchorEl(null);
    if (menuItem.onClick) {
      setTimeout(() => menuItem.onClick?.(), 0);
    }
  };

  // Calculate position based on anchor element
  const menuPosition = React.useMemo(() => {
    if (!anchorEl) return { top: 0, left: 0 };
    const rect = anchorEl.getBoundingClientRect();
    const menuWidth = 220;
    const windowWidth = window.innerWidth;

    // Open to the right of the anchor, clamp to viewport
    let left = rect.left;
    if (left + menuWidth > windowWidth - 8) {
      left = rect.right - menuWidth;
    }

    return {
      top: rect.bottom + 4,
      left,
    };
  }, [anchorEl]);

  // Close on outside click
  React.useEffect(() => {
    if (!open) return;
    const handleOutside = (e: MouseEvent) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(e.target as Node) &&
        anchorEl &&
        !anchorEl.contains(e.target as Node)
      ) {
        handleClose();
      }
    };
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, [open, anchorEl]);

  const visibleItems = menuOptions.filter((m) => !m.hide);

  const menu = open
    ? ReactDOM.createPortal(
        <Box
          ref={menuRef}
          sx={{
            position: "fixed",
            top: menuPosition.top,
            left: menuPosition.left,
            zIndex: 9999,
            width: 220,
            bgcolor: "#fff",
            borderRadius: "16px",
            boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
            overflow: "hidden",
            py: 0.5,
          }}
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
        >
          {visibleItems.map((menuItem, index) => (
            <React.Fragment key={menuItem.label}>
              <Box
                onClick={handleMenuItemClick(menuItem)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2.5,
                  px: 2.5,
                  py: 1,
                  cursor: menuItem.disabled ? "default" : "pointer",
                  opacity: menuItem.disabled ? 0.4 : 1,
                  transition: "background 0.12s",
                  "&:hover": menuItem.disabled
                    ? {}
                    : { bgcolor: "rgba(0,0,0,0.04)" },
                }}
              >
                {/* Icon */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    color: "text.secondary",
                    "& svg": { fontSize: "1.35rem" },
                  }}
                >
                  {menuItem.icon}
                </Box>

                {/* Label */}
                <Typography
                  sx={{
                    fontSize: "1rem",
                    fontWeight: 400,
                    color: "text.primary",
                    lineHeight: 1.4,
                    userSelect: "none",
                  }}
                >
                  {menuItem.label}
                </Typography>
              </Box>

              {/* Divider between items, not after last */}
              {index < visibleItems.length - 1 && (
                <Divider sx={{ mx: 2.5, borderColor: "rgba(0,0,0,0.06)" }} />
              )}
            </React.Fragment>
          ))}
        </Box>,
        document.body,
      )
    : null;

  return (
    <>
      <Tooltip title={title || "More"}>
        <IconButton
          size="small"
          onMouseDown={handleMouseDown}
          onClick={handleClick}
          sx={{ p: 0.25 }}
        >
          <MoreVert color="primary" fontSize="small" />
        </IconButton>
      </Tooltip>

      {menu}
    </>
  );
};
